import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import {
  IsLowercase,
  IsNotEmpty,
  IsOptional,
  IsString,
  Matches,
  MaxLength,
  MinLength,
} from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { toast } from 'react-toastify';

import type {
  ApplicationDto,
  CreateApplicationDto,
  OrganizationDto,
  ScopeDto,
  UpdateApplicationDto,
} from '@/shared/application/interfaces/pegasus-io.gateway';
import { ObservableForm } from '@/shared/presentation/forms/BaseForm';

import {
  APPLICATION_CREATED,
  CreateApplicationCollaboration,
} from '../../application/collaborations/create-application.collaboration';
import {
  APPLICATIONS_OBTAINED,
  GetApplicationsCollaboration,
} from '../../application/collaborations/get-applications.collaboration';
import {
  GetOrganizationsCollaboration,
  ORGANIZATIONS_OBTAINED,
} from '../../application/collaborations/get-organizations.collaboration';
import {
  APPLICATION_REMOVED,
  RemoveApplicationCollaboration,
} from '../../application/collaborations/remove-application.collaboration';
import {
  APPLICATION_UPDATED,
  UpdateApplicationCollaboration,
} from '../../application/collaborations/update-application.collaboration';

const subdomainRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

class Form {
  @IsOptional()
  guid: string;

  @IsString({ message: 'Campo obrigatório' })
  name: string;

  @MinLength(3, {
    message: 'Subdomínio pequeno demais',
  })
  @MaxLength(50, {
    message: 'Subdomínio grande demais',
  })
  @IsString({ message: 'Campo obrigatório' })
  @IsLowercase({ message: 'Campo deve estar em minúsculo' })
  @Matches(subdomainRegex, {
    message: 'Subdomínio inválido',
  })
  subdomain: string;

  @IsNotEmpty({ message: 'Campo obrigatório' })
  organization: OrganizationDto;

  @IsOptional()
  description: string;

  @IsOptional()
  scope: ScopeDto;
}

@ViewModel({ singleton: true })
export class ApplicationsViewModel implements IReactiveModel {
  @observable applications: ApplicationDto[] = [];
  @observable form = new ObservableForm(Form);
  @observable isLoading = false;
  @observable isUpdating = false;
  @observable isRefreshing = false;
  @observable hasError = false;
  @observable isRemoving = false;
  @observable isSubmitting = false;
  @observable organizations: OrganizationDto[];
  @observable isCreateModalOpen = false;
  @observable isUpdateModalOpen = false;

  constructor(
    private getOrganizationsCollaboration: GetOrganizationsCollaboration,
    private getApplicationsCollaboration: GetApplicationsCollaboration,
    private createApplicationCollaboration: CreateApplicationCollaboration,
    private updateApplicationCollaboration: UpdateApplicationCollaboration,
    private removeApplicationCollaboration: RemoveApplicationCollaboration,
  ) {
    makeAutoObservable(this);
  }

  get baseDomain() {
    if (process.env.REACT_APP_ENV === 'development') {
      return 'dev.descomplica.com.br';
    }

    if (process.env.REACT_APP_ENV === 'staging') {
      return 'io-stg.descomplica.com.br';
    }

    return 'io.descomplica.com.br';
  }

  @action
  async onInit() {
    try {
      this.isLoading = true;
      const [orgs, apps] = await Promise.all([
        this.getOrganizationsCollaboration.run(),
        this.getApplicationsCollaboration.run(),
      ]);

      if (orgs?.identifier === ORGANIZATIONS_OBTAINED) {
        this.organizations = orgs.data;
      }

      if (apps?.identifier === APPLICATIONS_OBTAINED) {
        this.applications = apps.data;
      }
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async refreshApplications() {
    try {
      this.isRefreshing = true;
      await this.onInit();
      this.isRefreshing = false;
    } catch (error) {
      console.error(error);
      toast.error('Erro ao atualiza aplicações');
    }
  }

  @action
  async create() {
    try {
      this.isSubmitting = true;

      const dto: CreateApplicationDto = {
        name: this.form.values.name,
        subdomain: this.form.values.subdomain,
        description: this.form.values.description,
        organizationGuid: this.form.values.organization.guid,
      };

      const newApp = await this.createApplicationCollaboration.run(dto);
      if (newApp?.identifier !== APPLICATION_CREATED) {
        throw new Error('Erro ao criar aplicação');
      }

      this.applications = [newApp.data, ...this.applications];

      this.form.reset();

      this.isCreateModalOpen = false;
      toast.success('Aplicação criada com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar aplicação');
    } finally {
      this.isSubmitting = false;
    }
  }

  async update() {
    try {
      this.isUpdating = true;

      const dto: UpdateApplicationDto = {
        application: {
          guid: this.form.values.guid,
          name: this.form.values.name,
          subdomain: this.form.values.subdomain,
          description: this.form.values.description,
          organizationGuid: this.form.values.organization.guid,
        },
        scope: this.form.values.scope,
      };

      const org = await this.updateApplicationCollaboration.run(dto);
      if (org?.identifier !== APPLICATION_UPDATED) {
        throw new Error('Erro ao atualizar aplicação');
      }

      this.applications = [
        org.data,
        ...this.applications.filter((organization) => organization.guid !== this.form.values.guid),
      ];

      this.form.reset();

      this.isUpdateModalOpen = false;

      toast.success('Aplicação atualizada com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao atualizar aplicação');
    } finally {
      this.isUpdating = false;
    }
  }

  @action
  async remove(guid: string, scopeGuid: string) {
    try {
      this.isRemoving = true;

      const org = await this.removeApplicationCollaboration.run({ guid, scopeGuid });
      if (org?.identifier !== APPLICATION_REMOVED) {
        throw new Error('Erro ao delete aplicação');
      }

      this.applications = this.applications.filter((application) => application.guid !== guid);

      toast.success('Aplicação removida com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover aplicação');
    } finally {
      this.isRemoving = false;
    }
  }

  @action
  openCreateModal() {
    this.isCreateModalOpen = true;
  }

  @action
  closeCreateModal() {
    this.isCreateModalOpen = false;
  }

  @action
  openUpdateModal(application: ApplicationDto) {
    const formOrganization = {
      guid: application.organization.guid,
      name: application.organization.name,
    };

    const formScope = {
      guid: application.scopeGuid,
      name: application.name,
    };

    this.form.setValue('guid', application.guid);
    this.form.setValue('name', application.name);
    this.form.setValue('subdomain', application.subdomain);
    this.form.setValue('organization', formOrganization);
    this.form.setValue('description', application.description);
    this.form.setValue('scope', formScope);

    this.isUpdateModalOpen = true;
  }

  @action
  closeUpdateModal() {
    this.form.reset();

    this.isUpdateModalOpen = false;
  }
}
